import { useState } from 'react'
import type { DemoRun } from '../mock/runs'
import { DEMO_RUNS, PLACEHOLDER_BALANCE, PLACEHOLDER_LOW_BALANCE } from '../mock/runs'
import type { JobPhase } from '../lib/jobClient'
import { MilliCoin } from '../icons'

/*
 * 演示条：后端不存在这段时间里，替后端做决定的那几个开关。
 *
 * 背景：这一页真正的输入只有一张图，但页面的走向还取决于两件页面自己决定不了的事——
 *   用户登没登录、余额够不够（都在 miyang.cn 网关那边），以及这张图的诊断结论
 *   （在首尔那台还没写的算力服务那边）。
 * 设计意图：把这两件事做成明写着「演示」的开关，而不是偷偷写死一种情况。
 *   写死的后果是只有一条路被走过，另一条上线当天才第一次被人看见。
 * 关键约束：
 *   1. 它是脚手架，不是产品的一部分。接后端时整个文件删掉，页面里去掉 <DemoBar/>
 *      与 session/run 两个 state，其余不动。在那之前它也不出现在普通访客面前：
 *      地址栏不提演示就整个不渲染（见 demoEnabled）。
 *   2. 九屏直达（?state=）留着。README 与 scripts/shoot_playground.sh 都按这个参数写，
 *      改名等于同时废掉截图脚本和已经发出去的链接。
 */

export type Session = { kind: 'guest' } | { kind: 'user'; balance: number }

export type ScreenId =
  | 'idle'
  | 'diagnosing'
  | 'regen_gate'
  | 'confirm_gate'
  | 'insufficient'
  | 'processing'
  | 'done_regen'
  | 'done_notch'
  | 'expired'
  | 'failed'
  | 'job_failed'

const GUEST: Session = { kind: 'guest' }
const USER: Session = { kind: 'user', balance: PLACEHOLDER_BALANCE }
const POOR: Session = { kind: 'user', balance: PLACEHOLDER_LOW_BALANCE }

/**
 * 十屏各自等于「哪一卷录像 + 什么身份 + 停在哪个阶段」。
 * 门禁在上传前由注册弹窗拦截（未登录不可上传）。
 */
export const SCREENS: Record<ScreenId, {
  label: string
  phase: JobPhase
  run: DemoRun['id']
  session: Session
}> = {
  idle: { label: '① 等待上传', phase: 'queued', run: 'regen', session: USER },
  diagnosing: { label: '② 诊断中', phase: 'diagnosing', run: 'regen', session: USER },
  regen_gate: { label: '③ 诊断完成：深度修复授权', phase: 'awaiting_confirm', run: 'regen', session: USER },
  // 真实用户最常撞见的其实是这一屏：诊断说滤波够用，只要点「开始处理」。
  // 它一直没有直达入口，于是每次改诊断闸都得真跑一趟才看得到（2026-09-11 补）。
  confirm_gate: { label: '③′ 诊断完成：无损清理', phase: 'awaiting_confirm', run: 'notch', session: USER },
  insufficient: { label: '④ 米粒不足', phase: 'awaiting_confirm', run: 'regen', session: POOR },
  processing: { label: '⑤ 处理中', phase: 'processing', run: 'regen', session: USER },
  done_regen: { label: '⑥ 完成（走了深度修复）', phase: 'done', run: 'regen', session: USER },
  done_notch: { label: '⑦ 完成（仅无损切除）', phase: 'done', run: 'notch', session: USER },
  expired: { label: '⑧ 授权超时', phase: 'expired', run: 'regen', session: USER },
  // 拒收发生在建作业之前，所以它没有作业阶段：页面停在首屏，只多一张拒收卡。
  failed: { label: '⑨ 图被拒收', phase: 'queued', run: 'regen', session: USER },
  job_failed: { label: '⑩ 处理中途失败', phase: 'failed', run: 'regen', session: USER },
}

const SCREEN_IDS = Object.keys(SCREENS) as ScreenId[]

/**
 * 演示条要不要出现在页面上。
 *
 * 2026-09-10 用户裁决：「右下角的那个演示的那个按钮，是不是可以删掉了呀」。
 * 真删不行——后端还没接上，登没登录、余额够不够、这张图诊断成什么样，三件事眼下
 * 只有这几个开关能回答，十一屏截图与 walk_flow.py 的十四步走查全靠它（见文件头）。
 *
 * 所以改成按需出现：地址栏没提演示，页面上就没有它，普通访客再也看不到脚手架。
 * 判据不只看 ?demo=1，还看 ?state= / ?as= / ?run= / ?pg=——那四个参数本身只有演示会用，
 * 带了就等于提了，于是已经发出去的直达链接一条都不用改。
 */
export function demoEnabled(): boolean {
  const q = new URLSearchParams(window.location.search)
  return ['demo', 'state', 'as', 'run', 'pg'].some(k => q.has(k))
}

/** URL 上的四个参数：?state= 直达某一屏，?as= 身份，?run= 哪一卷，?pg=1 显示屏幕列表。 */
export function readUrl(): { screen: ScreenId | null; session: Session; run: DemoRun['id']; pg: boolean } {
  const q = new URLSearchParams(window.location.search)
  const s = q.get('state')
  const screen = SCREEN_IDS.includes(s as ScreenId) ? (s as ScreenId) : null
  const as = q.get('as')
  const run = q.get('run') === 'notch' ? 'notch' : 'regen'
  // ?state= 自带身份与录像，显式的 ?as= / ?run= 只在没指定屏幕时说了算。
  const seeded = screen ? SCREENS[screen] : null
  return {
    screen,
    session: seeded ? seeded.session : as === 'guest' ? GUEST : as === 'poor' ? POOR : USER,
    run: seeded ? seeded.run : run,
    pg: q.get('pg') === '1',
  }
}

export function writeUrl(patch: { screen?: ScreenId | null; session?: Session; run?: DemoRun['id'] }) {
  const u = new URL(window.location.href)
  if ('screen' in patch) {
    if (patch.screen) u.searchParams.set('state', patch.screen)
    else u.searchParams.delete('state')
  }
  if (patch.session) {
    const v = patch.session.kind === 'guest' ? 'guest' : patch.session.balance < 400 ? 'poor' : 'user'
    u.searchParams.set('as', v)
  }
  if (patch.run) u.searchParams.set('run', patch.run)
  window.history.replaceState(null, '', u)
}

export function DemoBar({
  session, run, screen, showScreens, onSession, onRun, onScreen,
}: {
  session: Session
  run: DemoRun['id']
  screen: ScreenId | null
  showScreens: boolean
  onSession: (s: Session) => void
  onRun: (r: DemoRun['id']) => void
  onScreen: (s: ScreenId) => void
}) {
  const who = session.kind === 'guest' ? 'guest' : session.balance < 400 ? 'poor' : 'user'
  const [open, setOpen] = useState(false)

  // 地址栏没提演示就整个不渲染（见 demoEnabled）。开关的作用还在——?as= 照样换身份，
  // 只是那一条药丸不再压在页面右下角。
  if (!demoEnabled()) return null

  /* 收起态只是一枚角上的药丸。展开态是一条横贯底部的工具条，会盖住首屏下沿约 50 像素——
     首屏是这一页最要紧的一屏，脚手架不该常驻在那儿。 */
  if (!open) {
    return (
      <button className="demo-pill" onClick={() => setOpen(true)}>
        <span className="demo-tag">演示</span>
        {session.kind === 'guest' ? '访客' : `已登录 ${session.balance} 米粒`}
        {' · '}
        {DEMO_RUNS[run].label}
      </button>
    )
  }

  return (
    <div className="demo-bar">
      <div className="demo-bar-row">
        <span className="demo-tag">演示</span>
        <p>后端还没接上，这几个开关替它回答「登没登录、余额够不够、这张图诊断成什么样」。</p>
        <button className="demo-x" onClick={() => setOpen(false)} aria-label="收起演示条">收起</button>
      </div>

      <div className="demo-bar-row">
        <b>身份</b>
        <div className="seg">
          <button className={who === 'guest' ? 'on' : ''} onClick={() => onSession(GUEST)}>访客</button>
          <button className={who === 'user' ? 'on' : ''} onClick={() => onSession(USER)}>
            已登录<MilliCoin size={13} inline />{PLACEHOLDER_BALANCE}
          </button>
          <button className={who === 'poor' ? 'on' : ''} onClick={() => onSession(POOR)}>
            已登录<MilliCoin size={13} inline />{PLACEHOLDER_LOW_BALANCE}
          </button>
        </div>

        <b>这张图</b>
        <div className="seg">
          {(Object.keys(DEMO_RUNS) as DemoRun['id'][]).map(id => (
            <button key={id} className={run === id ? 'on' : ''} onClick={() => onRun(id)}>
              {DEMO_RUNS[id].label}
            </button>
          ))}
        </div>
      </div>

      {showScreens && (
        <div className="demo-bar-row demo-screens">
          <b>直达</b>
          {SCREEN_IDS.map(id => (
            <button
              key={id}
              className={`pg-tab${screen === id ? ' on' : ''}`}
              onClick={() => onScreen(id)}
            >
              {SCREENS[id].label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
